<script>
const MINUTE_IN_MILLI_SECONDS = 60000;
const HOUR_IN_MILLI_SECONDS = MINUTE_IN_MILLI_SECONDS * 60;
const DAY_IN_MILLI_SECONDS = HOUR_IN_MILLI_SECONDS * 24;

import {
  dynamicTime,
  dynamicTimeLocalized,
  dateFormatLocalized,
  shortTimestamp,
} from 'shared/helpers/timeHelper';

export default {
  name: 'TimeAgo',
  props: {
    isAutoRefreshEnabled: {
      type: Boolean,
      default: true,
    },
    lastActivityTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    createdAtTimestamp: {
      type: [String, Date, Number],
      default: '',
    },
    conversationId: {
      type: [String, Number],
      default: '',
    },
    // Aceleres -- cabecalho da conversa so quer "Ultima atividade ha 5
    // minutos" (o mesmo texto que ja existia no tooltip), nao o combo
    // compacto "criado • atividade" pensado pra linha da lista.
    lastActivityOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // English values feed the short display ("3d") via shortTimestamp().
      lastActivityAtTimeAgo: dynamicTime(this.lastActivityTimestamp),
      createdAtTimeAgo: dynamicTime(this.createdAtTimestamp),
      // Localized values feed the hover tooltip so it is not shown in English.
      lastActivityAtTimeAgoLong: dynamicTimeLocalized(
        this.lastActivityTimestamp
      ),
      createdAtTimeAgoLong: dynamicTimeLocalized(this.createdAtTimestamp),
      timer: null,
    };
  },
  computed: {
    lastActivityTime() {
      return shortTimestamp(this.lastActivityAtTimeAgo);
    },
    createdAtTime() {
      return shortTimestamp(this.createdAtTimeAgo);
    },
    createdAt() {
      const createdTimeDiff = Date.now() - this.createdAtTimestamp * 1000;
      const isBeforeAMonth = createdTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isBeforeAMonth
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.CREATED.LATEST')} ${
            this.createdAtTimeAgoLong
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.CREATED.OLDEST'
          )} ${dateFormatLocalized(this.createdAtTimestamp)}`;
    },
    lastActivity() {
      const lastActivityTimeDiff =
        Date.now() - this.lastActivityTimestamp * 1000;
      const isNotActive = lastActivityTimeDiff > DAY_IN_MILLI_SECONDS * 30;
      return !isNotActive
        ? `${this.$t('CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.ACTIVE')} ${
            this.lastActivityAtTimeAgoLong
          }`
        : `${this.$t(
            'CHAT_LIST.CHAT_TIME_STAMP.LAST_ACTIVITY.NOT_ACTIVE'
          )} ${dateFormatLocalized(this.lastActivityTimestamp)}`;
    },
    // Native title tooltip: rendered by the browser right at the cursor, so it
    // never drifts (the floating-vue tooltip could not be positioned correctly
    // for elements inside the virtualized conversation list / its transform).
    tooltipText() {
      return `${this.createdAt}\n${this.lastActivity}`;
    },
  },
  watch: {
    lastActivityTimestamp() {
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
      this.lastActivityAtTimeAgoLong = dynamicTimeLocalized(
        this.lastActivityTimestamp
      );
    },
    createdAtTimestamp() {
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
      this.createdAtTimeAgoLong = dynamicTimeLocalized(this.createdAtTimestamp);
    },
    conversationId() {
      // Reset display values and timer when the row is recycled to a different conversation.
      this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
      this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
      this.lastActivityAtTimeAgoLong = dynamicTimeLocalized(
        this.lastActivityTimestamp
      );
      this.createdAtTimeAgoLong = dynamicTimeLocalized(this.createdAtTimestamp);
      if (this.isAutoRefreshEnabled) {
        clearTimeout(this.timer);
        this.createTimer();
      }
    },
  },
  mounted() {
    if (this.isAutoRefreshEnabled) {
      this.createTimer();
    }
  },
  unmounted() {
    clearTimeout(this.timer);
  },
  methods: {
    createTimer() {
      this.timer = setTimeout(() => {
        this.lastActivityAtTimeAgo = dynamicTime(this.lastActivityTimestamp);
        this.createdAtTimeAgo = dynamicTime(this.createdAtTimestamp);
        this.lastActivityAtTimeAgoLong = dynamicTimeLocalized(
          this.lastActivityTimestamp
        );
        this.createdAtTimeAgoLong = dynamicTimeLocalized(
          this.createdAtTimestamp
        );
        this.createTimer();
      }, this.refreshTime());
    },
    refreshTime() {
      const timeDiff = Date.now() - this.lastActivityTimestamp * 1000;
      if (timeDiff > DAY_IN_MILLI_SECONDS) {
        return DAY_IN_MILLI_SECONDS;
      }
      if (timeDiff > HOUR_IN_MILLI_SECONDS) {
        return HOUR_IN_MILLI_SECONDS;
      }

      return MINUTE_IN_MILLI_SECONDS;
    },
  },
};
</script>

<template>
  <!-- Aceleres: sem escala nem cor proprias. Quem usa este modo e o cabecalho
       da conversa, onde esta linha divide a medida com a presenca do WhatsApp;
       fixar aqui um `text-sm` criava um segundo numero para a mesma linha, e
       era ele que vencia (o `class` de fora nao substitui utilitario de
       tamanho, apenas soma). -->
  <div v-if="lastActivityOnly" class="truncate">
    {{ lastActivity }}
  </div>
  <div
    v-else
    :title="tooltipText"
    class="ml-auto leading-4 text-xxs text-n-slate-10 hover:text-n-slate-11"
  >
    <span>{{ `${createdAtTime} • ${lastActivityTime}` }}</span>
  </div>
</template>
